import React, { useState, useContext } from "react";
import { Navigate } from "react-router-dom";
import { AuthContext } from "../../../authProvider/authProvider";
import { getAuth, signOut } from "firebase/auth";
import { host } from "../../../../config";
import CustomAlert from "components/generals/alert/CustomAlert";

import CircularProgress from "@mui/material/CircularProgress";
import { format } from "date-fns";
import DynamicForm from "components/generals/dynamicForm/DynamicForm";

const auth = getAuth();

const MasterEligibility: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAlertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error("AuthContext is undefined");
  }
  const { currentUser, suspended } = authContext;

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  let tk: string;
  currentUser
    ?.getIdToken(false)
    .then((idToken: string) => {
      tk = idToken;
    })
    .catch((error: any) => {
      console.log(error);
    });

  if (suspended) {
    signOut(auth)
      .then(() => {
        console.log("Signed out");
        return <Navigate to="/login" />;
      })
      .catch((error) => {
        console.error("Error signing out", error);
      });
  }

  const stateList = [
    "Select...",
    "AL",
    "AK",
    "AZ",
    "AR",
    "CA",
    "CO",
    "CT",
    "DE",
    "FL",
    "GA",
    "HI",
    "ID",
    "IL",
    "IN",
    "IA",
    "KS",
    "KY",
    "LA",
    "ME",
    "MD",
    "MA",
    "MI",
    "MN",
    "MS",
    "MO",
    "MT",
    "NE",
    "NV",
    "NH",
    "NJ",
    "NM",
    "NY",
    "NC",
    "ND",
    "OH",
    "OK",
    "OR",
    "PA",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "UT",
    "VT",
    "VA",
    "WA",
    "WV",
    "WI",
    "WY",
  ];

  const formSchema = {
    eligibilityDates: {
      title: "Eligibility Date Range",
      fields: {
        startDate: {
          form_type: "date",
          label: "Start Date",
          pair_with: "endDate",
        },
        endDate: {
          form_type: "date",
          label: "End Date",
        },
      },
    },
    additionalFields: {
      title: "Additional Fields",
      fields: {
        carrierId: {
          form_type: "selection",
          label: "Carrier ID",
          selections: [
            "Select...",
            "FSL",
            "SelfFunded",
            "Breckpoint",
            "Shelterpoint",
            "Nationwide",
            "AST",
            "MEC",
            "IHPMEC",
            "Zurich",
            "GERBER",
            "CHUBB",
            "MARKEL",
            "EVEREST",
            "HIIG",
            "IAIC Premium",
            "MULTI CARRIER",
          ],
        },
        product: {
          form_type: "selection",
          label: "Product",
          selections: [
            "Select...",
            "IHP",
            "EMEC",
            "RX",
            "ME",
            "GAP",
            "DE",
            "MEC",
            "LM",
            "DEP",
            "MEC-LM",
            "LF",
            "Other",
          ],
        },
        groupState: {
          form_type: "selection",
          label: "Group State",
          selections: stateList,
        },
        memberState: {
          form_type: "selection",
          label: "Member State",
          selections: stateList,
        },
      },
    },
  };

  const handleSubmit = (formData) => {
    // e.preventDefault();
    setIsLoading(false);
    // setShowApiData(true);

    // Check if at least one field has a value
    const hasAtLeastOneValue =
      formData["startDate"] ||
      formData["endDate"] ||
      formData["carrierId"] ||
      formData["product"] ||
      formData["groupState"] ||
      formData["memberState"];

    if (!hasAtLeastOneValue) {
      setAlertMessage("Please fill in at least one search criteria!");
      setAlertOpen(true);
      return;
    }

    // Validate date ranges if either date in a pair is provided
    if ((formData["startDate"] && !formData["endDate"]) || (!formData["startDate"] && formData["endDate"])) {
      setAlertMessage("Please provide both start and end dates for Service Date Range!");
      setAlertOpen(true);
      return;
    }

    // If dates are provided, validate the ranges
    if (formData["startDate"] && formData["endDate"]) {
      const startDateObj = formData["startDate"];
      const endDateObj = formData["endDate"];

      if (endDateObj < startDateObj) {
        setAlertMessage("Service End date must be after Service Start date!");
        setAlertOpen(true);
        return;
      }

      // Check if date range is more than 18 months
      const monthsDiff =
        (endDateObj.getFullYear() - startDateObj.getFullYear()) * 12 +
        (endDateObj.getMonth() - startDateObj.getMonth());

      if (monthsDiff > 18) {
        setAlertMessage("Service Date range cannot exceed 18 months!");
        setAlertOpen(true);
        return;
      }
    }

    // Validate paid date range if provided

    let holder: { [key: string]: any } = {};

    // Only add parameters that have values
    if (formData["startDate"]) holder.startDate = format(formData["startDate"], "yyyy-MM-dd");
    if (formData["endDate"]) holder.endDate = format(formData["endDate"], "yyyy-MM-dd");
    if (formData["carrierId"]) holder.carrierId = formData["carrierId"];
    if (formData["product"]) holder.facilityState = formData["facilityState"];
    if (formData["groupState"]) holder.groupState = formData["groupState"];
    if (formData["memberState"]) holder.memberState = formData["memberState"];

    let data = {
      job: "master-eligibility-test",
      className: "com.ugp.jobs.masterEligibility.MasterEligibility",
      parameterString: JSON.stringify(holder),
    };

    setIsLoading(true);

    fetch(`${host}/api/Reporting/Dataproc/Create/CreateDataProc`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${tk}`,
      },
      body: JSON.stringify(data),
    })
      .then(async (response: Response) => {
        if (!response.ok) {
          await response.text().then((text) => {
            setAlertMessage(text || "An error occurred with your query");
            setAlertOpen(true);
          });
          return;
        } else {
          await response.text().then(() => {
            setAlertMessage(
              "Your report has been requested. Please check your email in a few minutes for the results."
            );
            setAlertOpen(true);
          });
        }
      })
      .catch((error: any) => {
        console.log(error);
        setAlertMessage("An error has occurred when searching for Prompt Pay");
        setAlertOpen(true);
      })
      .finally(() => {
        setIsLoading(false);
        console.log("Processing finished");
      });
  };

  if (isLoading) {
    return (
      <div className="loader-container">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="master-eligibility-container">
      <CustomAlert message={alertMessage} isOpen={isAlertOpen} onClose={() => setAlertOpen(false)} />
      <DynamicForm
        formSchema={formSchema}
        onSubmit={handleSubmit}
        title="Master Elgibility Report"
        submitButtonText="Submit"
      />
    </div>
  );
};

export default MasterEligibility;
